import React, { useState } from 'react';
import api from '../api/client';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';

const detail = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fb;
};

export const SuperAdminPasswordChange = () => {
  const [step, setStep] = useState('idle'); // idle | verify
  const [busy, setBusy] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [guardian, setGuardian] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const requestCode = async () => {
    if (!currentPassword) {
      toast.error('Error', { description: 'Enter your current password first' });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/auth/superadmin/password-change/request', { currentPassword });
      setGuardian(r.data.guardian);
      if (r.data._devCode) setCode(r.data._devCode);
      setStep('verify');
      toast.success('Code sent', { description: r.data.message });
    } catch (e) {
      toast.error('Error', { description: detail(e, 'Could not send the verification code') });
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.trim().length !== 6) {
      toast.error('Error', { description: 'Enter the 6-digit code' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Error', { description: 'New passwords do not match' });
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/auth/superadmin/password-change/confirm', { code: code.trim(), newPassword });
      toast.success('Password changed', { description: r.data.message });
      setStep('idle'); setCurrentPassword(''); setCode(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      toast.error('Error', { description: detail(e, 'Could not change the password') });
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t pt-4" data-testid="superadmin-password-change">
      <p className="text-sm font-medium mb-1 flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-orange-500" /> Change Password (verified)
      </p>
      <p className="text-xs text-gray-500 mb-3">
        SuperAdmin password changes require a 6-digit code sent to the designated guardian.
      </p>
      {step === 'idle' ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="sa-current-password">Current password</Label>
            <Input id="sa-current-password" type="password" value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="sa-current-password-input" />
          </div>
          <Button size="sm" onClick={requestCode} disabled={busy} data-testid="sa-request-code-btn">
            {busy ? 'Sending…' : 'Send verification code'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5"
            data-testid="sa-guardian-note">
            Code sent to guardian {guardian}. It expires in 10 minutes.
          </p>
          <div className="space-y-2">
            <Label htmlFor="sa-otp-code">6-digit verification code</Label>
            <Input id="sa-otp-code" inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              data-testid="sa-otp-code-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sa-new-password">New password (min 16 chars, letters + digits)</Label>
            <Input id="sa-new-password" type="password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="sa-new-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sa-confirm-password">Confirm new password</Label>
            <Input id="sa-confirm-password" type="password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="sa-confirm-password-input" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={confirm} disabled={busy} data-testid="sa-confirm-change-btn">
              {busy ? 'Changing…' : 'Change password'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setStep('idle')} disabled={busy}
              data-testid="sa-cancel-change-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
